import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, switchMap } from 'rxjs/operators';

import {
    CandidateQuery,
    GableKnowledgeService,
    KnowledgeDraft,
} from '../../../../../services/gable/gable-knowledge.service';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import {
    ConfirmActionV2DialogComponent,
    ConfirmActionV2DialogInput,
} from '../../../shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { EmptyStateModule } from '../../../shared/empty-state/empty-state.module';
import { SimpleBadgeComponent } from '../../../shared/simple-badge/simple-badge.component';
import { FlexSpacerDirective } from '../../../shared/directives/flex-spacer.directive';

/** A mined draft plus its inline-edit form and collapsible state. */
interface DraftCard {
    draft: KnowledgeDraft;
    form: FormGroup;
    /** Whether the evidence sample block is expanded. */
    evidenceExpanded: boolean;
    /** Whether an approve/reject/save request is in flight for this card. */
    busy: boolean;
}

/** A candidate verified query plus its in-flight flag. */
interface QueryCard {
    query: CandidateQuery;
    busy: boolean;
}

export type DraftReviewKind = 'query' | 'skill' | 'lesson';

/**
 * One review section for a single kind of Gable-proposed artifact, embedded
 * on the surface where that artifact lives (SD-9309): candidate verified
 * queries on the Verified queries tab, skill drafts on the Skills panel,
 * mined draft lessons on the Lessons tab. Replaces the old centralized
 * Review Queue tab. Super-only on the backend.
 */
@UntilDestroy()
@Component({
    selector: 'app-gable-draft-review',
    templateUrl: './draft-review.component.html',
    styleUrls: ['./draft-review.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        EmptyStateModule,
        SimpleBadgeComponent,
        FlexSpacerDirective,
    ],
})
export class GableDraftReviewComponent implements OnInit {
    @Input({ required: true }) kind!: DraftReviewKind;

    /** Render an empty-state card when there is nothing to review (the Lessons "Needs review" view wants one; inline strips don't). */
    @Input() showEmptyState = false;

    @Output() countChanged = new EventEmitter<number>();

    draftCards: DraftCard[] = [];
    queryCards: QueryCard[] = [];
    loading = false;

    constructor(
        private readonly _knowledge: GableKnowledgeService,
        private readonly _dialog: MatDialog,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _growler: GrowlerService,
        private readonly _fb: FormBuilder,
    ) {}

    ngOnInit(): void {
        this.refresh();
    }

    refresh(): void {
        this.loading = true;
        this._cdr.markForCheck();

        this._knowledge
            .listDrafts()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: ({ drafts, candidateQueries }) => {
                    this.draftCards = this.buildDraftCards(
                        (drafts ?? []).filter((d) =>
                            this.kind === 'skill'
                                ? d.artifactType === 'skill'
                                : d.artifactType !== 'skill',
                        ),
                    );
                    this.queryCards = (candidateQueries ?? []).map((query) => ({
                        query,
                        busy: false,
                    }));
                    this.loading = false;
                    this.emitCount();
                    this._cdr.markForCheck();
                },
                error: () => {
                    this.loading = false;
                    this._growler.error('Gable review', 'Could not load the items awaiting review.');
                    this._cdr.markForCheck();
                },
            });
    }

    get count(): number {
        return this.kind === 'query' ? this.queryCards.length : this.draftCards.length;
    }

    get isEmpty(): boolean {
        return !this.loading && this.count === 0;
    }

    sightingsCount(draft: KnowledgeDraft): number {
        return draft.evidence?.sightings?.length ?? 0;
    }

    // --- Mined drafts -------------------------------------------------------

    /** Human-readable label for the signal that surfaced a draft. */
    signalLabel(draft: KnowledgeDraft): string {
        const ev = draft.evidence;
        if (!ev) { return 'Mined signal'; }
        if (ev.signal === 'bad_feedback') { return 'Repeated thumbs-down'; }
        if (ev.signal === 'failed_sql') {
            return ev.view ? `Failed queries on ${ev.view}` : 'Failed queries';
        }
        return 'Mined signal';
    }

    signalColor(draft: KnowledgeDraft): 'danger' | 'warning' | 'muted' {
        const signal = draft.evidence?.signal;
        if (signal === 'bad_feedback') { return 'danger'; }
        if (signal === 'failed_sql') { return 'warning'; }
        return 'muted';
    }

    signalIcon(draft: KnowledgeDraft): string {
        const signal = draft.evidence?.signal;
        if (signal === 'bad_feedback') { return 'ph-thumbs-down'; }
        if (signal === 'failed_sql') { return 'ph-warning-circle'; }
        return 'ph-sparkle';
    }

    hasEvidenceSamples(draft: KnowledgeDraft): boolean {
        const ev = draft.evidence;
        return !!(ev && ((ev.sampleQuestions?.length ?? 0) > 0 || (ev.sampleSql?.length ?? 0) > 0));
    }

    toggleEvidence(card: DraftCard): void {
        card.evidenceExpanded = !card.evidenceExpanded;
        this._cdr.markForCheck();
    }

    /** Lessons require written guidance before approving; skill drafts carry their own body. */
    canApproveDraft(card: DraftCard): boolean {
        if (card.busy) { return false; }
        if (card.draft.artifactType === 'skill') {
            return ((card.draft.proposedPayload?.body ?? '') as string).trim().length > 0;
        }
        const guidance = (card.form.get('guidance')?.value ?? '').trim();
        return guidance.length > 0;
    }

    saveDraft(card: DraftCard): void {
        if (card.busy) { return; }
        const value = card.form.getRawValue();
        card.busy = true;
        this._cdr.markForCheck();

        this._knowledge
            .updateDraft(card.draft.id, {
                title: (value.title ?? '').trim(),
                triggerContext: (value.triggerContext ?? '').trim(),
                guidance: (value.guidance ?? '').trim(),
            })
            .pipe(untilDestroyed(this))
            .subscribe({
                next: ({ draft }) => {
                    // replaceDraft swaps in a fresh card object (busy: false);
                    // mutating the now-orphaned `card` would leave the rendered
                    // card stuck busy, so let replaceDraft own the reset.
                    this.replaceDraft(card, draft);
                    this._growler.success('Gable review', 'Draft saved.');
                    this._cdr.markForCheck();
                },
                error: (err: HttpErrorResponse) => {
                    card.busy = false;
                    this._growler.fromErrorResponse(err, 'Could not save the draft.');
                    this._cdr.markForCheck();
                },
            });
    }

    approveDraft(card: DraftCard): void {
        if (card.busy) { return; }
        // Guard locally, but still handle the 422 — guidance may be blank
        // server-side if it was never saved.
        if (!this.canApproveDraft(card)) {
            this._growler.error(
                'Gable review',
                card.draft.artifactType === 'skill'
                    ? 'This skill draft has no playbook body to approve.'
                    : 'Write the guidance fix before approving this lesson.',
            );
            return;
        }

        card.busy = true;
        this._cdr.markForCheck();

        this._knowledge
            .approveDraft(card.draft.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.removeDraft(card);
                    this._growler.success(
                        'Gable review',
                        card.draft.artifactType === 'skill'
                            ? 'Skill approved and promoted.'
                            : 'Lesson approved and promoted.',
                    );
                    this._cdr.markForCheck();
                },
                error: (err: HttpErrorResponse) => {
                    card.busy = false;
                    if (err.status === 422) {
                        // Backend rejected for empty title/trigger/guidance.
                        this._growler.fromErrorResponse(
                            err,
                            'Fill in the title, trigger, and guidance before approving.',
                        );
                    } else {
                        this._growler.fromErrorResponse(err, 'Could not approve the draft.');
                    }
                    this._cdr.markForCheck();
                },
            });
    }

    rejectDraft(card: DraftCard): void {
        if (card.busy) { return; }
        const title =
            (card.form.get('title')?.value ?? '').trim() ||
            card.draft.proposedPayload?.name ||
            'this draft';
        const isSkill = card.draft.artifactType === 'skill';
        const input: ConfirmActionV2DialogInput = {
            title: isSkill ? 'Reject this proposed skill?' : 'Reject this draft lesson?',
            content:
                `Rejecting "${title}" dismisses the proposal. Gable won't ` +
                'learn from this signal cluster, though the underlying evidence stays in ' +
                'the activity log.',
            confirmButton: 'Reject draft',
        };
        this._dialog
            .open(ConfirmActionV2DialogComponent, {
                panelClass: ['v2-dialog'],
                width: ConfirmActionV2DialogComponent.width,
                data: input,
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result?.success),
                switchMap(() => {
                    card.busy = true;
                    this._cdr.markForCheck();
                    return this._knowledge.rejectDraft(card.draft.id);
                }),
                untilDestroyed(this),
            )
            .subscribe({
                next: () => {
                    this.removeDraft(card);
                    this._growler.success('Gable review', 'Draft rejected.');
                    this._cdr.markForCheck();
                },
                error: (err: HttpErrorResponse) => {
                    card.busy = false;
                    this._growler.fromErrorResponse(err, 'Could not reject the draft.');
                    this._cdr.markForCheck();
                },
            });
    }

    // --- Candidate verified queries -----------------------------------------

    approveQuery(card: QueryCard): void {
        if (card.busy) { return; }
        card.busy = true;
        this._cdr.markForCheck();

        this._knowledge
            .approveVerifiedQuery(card.query.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.removeQuery(card);
                    this._growler.success('Gable review', 'Query promoted to verified.');
                    this._cdr.markForCheck();
                },
                error: (err: HttpErrorResponse) => {
                    card.busy = false;
                    this._growler.fromErrorResponse(err, 'Could not approve the query.');
                    this._cdr.markForCheck();
                },
            });
    }

    rejectQuery(card: QueryCard): void {
        if (card.busy) { return; }
        const input: ConfirmActionV2DialogInput = {
            title: 'Retire this candidate query?',
            content:
                `Retiring "${card.query.title}" dismisses the candidate. Gable ` +
                'will not promote this SQL to a verified query.',
            confirmButton: 'Retire query',
        };
        this._dialog
            .open(ConfirmActionV2DialogComponent, {
                panelClass: ['v2-dialog'],
                width: ConfirmActionV2DialogComponent.width,
                data: input,
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result?.success),
                switchMap(() => {
                    card.busy = true;
                    this._cdr.markForCheck();
                    return this._knowledge.rejectVerifiedQuery(card.query.id);
                }),
                untilDestroyed(this),
            )
            .subscribe({
                next: () => {
                    this.removeQuery(card);
                    this._growler.success('Gable review', 'Candidate query retired.');
                    this._cdr.markForCheck();
                },
                error: (err: HttpErrorResponse) => {
                    card.busy = false;
                    this._growler.fromErrorResponse(err, 'Could not retire the query.');
                    this._cdr.markForCheck();
                },
            });
    }

    // --- Card list helpers (immutable updates for OnPush) -------------------

    private emitCount(): void {
        this.countChanged.emit(this.count);
    }

    private buildDraftCards(drafts: KnowledgeDraft[]): DraftCard[] {
        return [...drafts]
            .sort((a, b) => b.priority - a.priority)
            .map((draft) => ({
                draft,
                form: this.buildDraftForm(draft),
                evidenceExpanded: false,
                busy: false,
            }));
    }

    private buildDraftForm(draft: KnowledgeDraft): FormGroup {
        const payload = draft.proposedPayload ?? {};
        return this._fb.group({
            title: this._fb.control(payload.title ?? ''),
            triggerContext: this._fb.control(payload.triggerContext ?? ''),
            // Guidance intentionally starts empty unless the human already
            // wrote one in a previous session.
            guidance: this._fb.control(payload.guidance ?? ''),
        });
    }

    /** Swap a freshly-saved draft into its card, keeping the live form. */
    private replaceDraft(card: DraftCard, updated: KnowledgeDraft): void {
        this.draftCards = this.draftCards.map((c) =>
            c.draft.id === card.draft.id ? { ...c, draft: updated, busy: false } : c,
        );
    }

    private removeDraft(card: DraftCard): void {
        this.draftCards = this.draftCards.filter((c) => c.draft.id !== card.draft.id);
        this.emitCount();
    }

    private removeQuery(card: QueryCard): void {
        this.queryCards = this.queryCards.filter((c) => c.query.id !== card.query.id);
        this.emitCount();
    }

    // --- trackBy ------------------------------------------------------------

    trackByDraftId(_: number, card: DraftCard): number {
        return card.draft.id;
    }

    trackByQueryId(_: number, card: QueryCard): number {
        return card.query.id;
    }

    trackByIndex(index: number): number {
        return index;
    }
}
